import React, { useEffect, useMemo, useState } from 'react';
import { Wallet, ArrowUpRight, ArrowDownLeft, Receipt, Clock, CheckCircle2, XCircle, RotateCcw, Plus, ShoppingBag, ArrowUpDown, ExternalLink, X, CreditCard, Smartphone, ChevronDown, ArrowLeft, Shield, Loader2, Check, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { useLanguage } from '../../i18n/LanguageContext';
interface CreditsTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
type TxType = 'topup' | 'payment' | 'refund';
type TxStatus = 'success' | 'pending' | 'failed' | 'refunded';
interface Transaction {
  id: string;
  date: string;
  dateSortKey: number;
  type: TxType;
  description: string;
  orderId?: string;
  amount: number;
  status: TxStatus;
  method?: string;
}
export function CreditsTab({
  theme,
  onNavigate
}: CreditsTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [filter, setFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState<'new' | 'old'>('new');
  const [expandedTx, setExpandedTx] = useState<string | null>(null);
  const [topupStep, setTopupStep] = useState(0);
  const [topupAmount, setTopupAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cardName, setCardName] = useState('');
  const [saveCard, setSaveCard] = useState(true);
  const [smsCode, setSmsCode] = useState('');
  const [smsTimer, setSmsTimer] = useState(60);
  const [smsSent, setSmsSent] = useState(false);
  const balance = 7000;
  const transactions: Transaction[] = [{
    id: 'TX-1042',
    date: '12 Окт 2024, 14:30',
    dateSortKey: 202410121430,
    type: 'payment',
    description: t('userdash.credits.txConsultation'),
    orderId: 'ORD-1042',
    amount: -5000,
    status: 'success',
    method: t('userdash.credits.methodBalance')
  }, {
    id: 'TX-1041',
    date: '10 Окт 2024, 09:15',
    dateSortKey: 202410100915,
    type: 'topup',
    description: t('userdash.credits.txTopup'),
    amount: 10000,
    status: 'success',
    method: 'Visa •••• 4242'
  }, {
    id: 'TX-1040',
    date: '08 Окт 2024, 16:00',
    dateSortKey: 202410081600,
    type: 'payment',
    description: t('userdash.credits.txReportSolar'),
    orderId: 'ORD-1040',
    amount: -2990,
    status: 'success',
    method: t('userdash.credits.methodBalance')
  }, {
    id: 'TX-1039',
    date: '05 Окт 2024, 18:20',
    dateSortKey: 202410051820,
    type: 'payment',
    description: t('userdash.credits.txReportNatal'),
    orderId: 'ORD-1039',
    amount: -3500,
    status: 'success',
    method: t('userdash.credits.methodBalance')
  }, {
    id: 'TX-1038',
    date: '01 Окт 2024, 11:00',
    dateSortKey: 202410011100,
    type: 'refund',
    description: t('userdash.credits.txRefund'),
    orderId: 'ORD-1037',
    amount: 7000,
    status: 'refunded',
    method: t('userdash.credits.methodToBalance')
  }, {
    id: 'TX-1037',
    date: '28 Сен 2024, 16:45',
    dateSortKey: 202409281645,
    type: 'payment',
    description: t('userdash.credits.txConsultationBusiness'),
    orderId: 'ORD-1037',
    amount: -7000,
    status: 'success',
    method: t('userdash.credits.methodBalance')
  }, {
    id: 'TX-1036',
    date: '25 Сен 2024, 10:10',
    dateSortKey: 202409251010,
    type: 'topup',
    description: t('userdash.credits.txTopup'),
    amount: 5000,
    status: 'success',
    method: 'Apple Pay'
  }, {
    id: 'TX-1035',
    date: '20 Сен 2024, 14:00',
    dateSortKey: 202409201400,
    type: 'topup',
    description: t('userdash.credits.txTopupError'),
    amount: 1500,
    status: 'failed',
    method: 'Visa •••• 4242'
  }, {
    id: 'TX-1034',
    date: '15 Сен 2024, 12:30',
    dateSortKey: 202409151230,
    type: 'payment',
    description: t('userdash.credits.txCourse'),
    orderId: 'ORD-1034',
    amount: -15000,
    status: 'pending',
    method: t('userdash.credits.methodBalance')
  }];
  const TOPUP_AMOUNTS = [1000, 3000, 5000, 10000];
  const STATUS_CONFIG: Record<TxStatus, {
    label: string;
    variant: 'success' | 'warning' | 'error' | 'neutral';
    icon: React.ReactNode;
  }> = {
    success: {
      label: t('userdash.credits.statusSuccess'),
      variant: 'success',
      icon: <CheckCircle2 className="w-3 h-3" data-id="element-18385" />
    },
    pending: {
      label: t('userdash.credits.statusPending'),
      variant: 'warning',
      icon: <Clock className="w-3 h-3" data-id="element-18386" />
    },
    failed: {
      label: t('userdash.credits.statusFailed'),
      variant: 'error',
      icon: <XCircle className="w-3 h-3" data-id="element-18387" />
    },
    refunded: {
      label: t('userdash.credits.statusRefunded'),
      variant: 'neutral',
      icon: <RotateCcw className="w-3 h-3" data-id="element-18388" />
    }
  };
  const FILTER_TABS = [{
    key: 'all',
    label: t('userdash.credits.filterAll')
  }, {
    key: 'topup',
    label: t('userdash.credits.filterTopup')
  }, {
    key: 'payment',
    label: t('userdash.credits.filterPayment')
  }, {
    key: 'refund',
    label: t('userdash.credits.filterRefund')
  }];
  const PAYMENT_METHODS = [{
    id: 'card',
    label: t('userdash.credits.methodCard'),
    desc: 'Visa, Mastercard, МИР',
    icon: <CreditCard className="w-5 h-5" data-id="element-18389" />
  }, {
    id: 'apple',
    label: 'Apple Pay',
    desc: t('userdash.credits.methodFast'),
    icon: <Smartphone className="w-5 h-5" data-id="element-18390" />
  }, {
    id: 'google',
    label: 'Google Pay',
    desc: t('userdash.credits.methodFast'),
    icon: <Smartphone className="w-5 h-5" data-id="element-18391" />
  }];
  function formatAmount(amount: number): string {
    const abs = Math.abs(amount).toLocaleString('ru-RU');
    return amount >= 0 ? `+${abs} ₽` : `-${abs} ₽`;
  }
  const filtered = useMemo(() => {
    let result = [...transactions];
    if (filter !== 'all') result = result.filter(t => t.type === filter);
    result.sort((a, b) => sortOrder === 'new' ? b.dateSortKey - a.dateSortKey : a.dateSortKey - b.dateSortKey);
    return result;
  }, [filter, sortOrder]);
  const totalSpent = transactions.filter(t => t.type === 'payment' && t.status === 'success').reduce((s, t) => s + Math.abs(t.amount), 0);
  const totalTopup = transactions.filter(t => t.type === 'topup' && t.status === 'success').reduce((s, t) => s + t.amount, 0);
  const totalRefund = transactions.filter(t => t.type === 'refund').reduce((s, t) => s + t.amount, 0);
  const finalAmount = topupAmount || parseInt(customAmount) || 0;
  const resetTopup = () => {
    setTopupStep(0);
    setTopupAmount(null);
    setCustomAmount('');
    setPaymentMethod(null);
    setCardNumber('');
    setCardExpiry('');
    setCardCvv('');
    setCardName('');
    setSmsCode('');
    setSmsTimer(60);
    setSmsSent(false);
  };
  useEffect(() => {
    if (topupStep === 4 && smsTimer > 0) {
      const t = setTimeout(() => setSmsTimer(v => v - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [topupStep, smsTimer]);
  useEffect(() => {
    if (topupStep === 4 && !smsSent) {
      setSmsSent(true);
      setSmsTimer(60);
    }
  }, [topupStep, smsSent]);
  useEffect(() => {
    if (topupStep === 5) {
      const t = setTimeout(() => setTopupStep(6), 2500);
      return () => clearTimeout(t);
    }
  }, [topupStep]);
  const formatCardNum = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };
  const formatExp = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    if (digits.length > 2) return digits.slice(0, 2) + '/' + digits.slice(2);
    return digits;
  };
  const isCardValid = cardNumber.replace(/\s/g, '').length === 16 && cardExpiry.length === 5 && cardCvv.length >= 3 && cardName.length >= 2;
  return <div className="space-y-5 max-w-3xl pb-8" data-id="element-18392">
      <div data-id="element-18393">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-18394">
          {t('userdash.credits.title')}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-18395">
          {t('userdash.credits.subtitle')}
        </p>
      </div>

      {/* Balance Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm" data-id="element-18396">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-18397">
          <div className="flex items-center gap-4" data-id="element-18398">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0" data-id="element-18399">
              <Wallet className="w-6 h-6 text-indigo-600" data-id="element-18400" />
            </div>
            <div data-id="element-18401">
              <div className="text-xs text-slate-500 font-medium" data-id="element-18402">
                {t('userdash.credits.balance')}
              </div>
              <div className="text-3xl font-bold text-slate-900" data-id="element-18403">
                {balance.toLocaleString('ru-RU')}{' '}
                <span className="text-lg text-slate-400" data-id="element-18404">₽</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-18405">
            <Button size="sm" className="text-xs" onClick={() => {
            if (topupStep === 0) setTopupStep(1);else resetTopup();
          }} data-id="element-18406">
              <Plus className="w-3.5 h-3.5 mr-1" data-id="element-18407" />{' '}
              {t('userdash.credits.topUp')}
            </Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => onNavigate('home')} data-id="element-18408">
              <ShoppingBag className="w-3.5 h-3.5 mr-1" data-id="element-18409" />{' '}
              {t('userdash.credits.payService')}
            </Button>
          </div>
        </div>
      </div>

      {/* Top-up Flow */}
      {topupStep > 0 && <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-18410">
          {topupStep < 6 && <div className="h-1 bg-slate-100" data-id="element-18411">
              <div className="h-1 bg-indigo-500 transition-all duration-500" style={{
          width: `${Math.min(topupStep, 5) / 5 * 100}%`
        }} data-id="element-18412" />
            </div>}

          <div className="p-4 space-y-4" data-id="element-18413">
            {/* Step 1: Amount */}
            {topupStep === 1 && <>
                <div className="flex items-center justify-between" data-id="element-18414">
                  <h4 className="font-bold text-slate-900 text-sm" data-id="element-18415">
                    {t('userdash.credits.chooseAmount')}
                  </h4>
                  <button onClick={resetTopup} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18416">
                    <X className="w-4 h-4" data-id="element-18417" />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-18418">
                  {TOPUP_AMOUNTS.map(amt => <button key={amt} onClick={() => {
              setTopupAmount(amt);
              setCustomAmount('');
            }} className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors border ${topupAmount === amt ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-id="element-18419">
                      {amt.toLocaleString('ru-RU')} ₽
                    </button>)}
                </div>
                <div className="relative" data-id="element-18420">
                  <input type="number" placeholder={t('userdash.credits.otherAmount')} value={customAmount} onChange={e => {
              setCustomAmount(e.target.value);
              setTopupAmount(null);
            }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-18421" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" data-id="element-18422">
                    ₽
                  </span>
                </div>
                <Button size="sm" className="w-full text-xs" disabled={!finalAmount || finalAmount <= 0} onClick={() => setTopupStep(2)} data-id="element-18423">
                  {t('userdash.credits.nextChooseMethod')}
                </Button>
              </>}

            {/* Step 2: Payment Method */}
            {topupStep === 2 && <>
                <div className="flex items-center justify-between" data-id="element-18424">
                  <div className="flex items-center gap-2" data-id="element-18425">
                    <button onClick={() => setTopupStep(1)} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18426">
                      <ArrowLeft className="w-4 h-4" data-id="element-18427" />
                    </button>
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-18428">
                      {t('userdash.credits.paymentMethod')}
                    </h4>
                  </div>
                  <button onClick={resetTopup} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18429">
                    <X className="w-4 h-4" data-id="element-18430" />
                  </button>
                </div>
                <div className="text-xs text-slate-500" data-id="element-18431">
                  {t('userdash.credits.amount')}:{' '}
                  <span className="font-medium text-slate-900" data-id="element-18432">
                    {finalAmount.toLocaleString('ru-RU')} ₽
                  </span>
                </div>
                <div className="space-y-2" data-id="element-18433">
                  {PAYMENT_METHODS.map(m => <button key={m.id} onClick={() => setPaymentMethod(m.id)} className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${paymentMethod === m.id ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-18434">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${paymentMethod === m.id ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-18435">
                        {m.icon}
                      </div>
                      <div data-id="element-18436">
                        <div className="text-sm font-medium text-slate-900" data-id="element-18437">
                          {m.label}
                        </div>
                        <div className="text-[10px] text-slate-500" data-id="element-18438">
                          {m.desc}
                        </div>
                      </div>
                      {paymentMethod === m.id && <CheckCircle2 className="w-4 h-4 text-indigo-600 ml-auto shrink-0" data-id="element-18439" />}
                    </button>)}
                </div>
                <Button size="sm" className="w-full text-xs" disabled={!paymentMethod} onClick={() => {
            if (paymentMethod === 'card') setTopupStep(3);else setTopupStep(4);
          }} data-id="element-18440">
                  {t('userdash.credits.next')}
                </Button>
              </>}

            {/* Step 3: Card Details */}
            {topupStep === 3 && <>
                <div className="flex items-center justify-between" data-id="element-18441">
                  <div className="flex items-center gap-2" data-id="element-18442">
                    <button onClick={() => setTopupStep(2)} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18443">
                      <ArrowLeft className="w-4 h-4" data-id="element-18444" />
                    </button>
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-18445">
                      {t('userdash.credits.cardDetails')}
                    </h4>
                  </div>
                  <button onClick={resetTopup} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18446">
                    <X className="w-4 h-4" data-id="element-18447" />
                  </button>
                </div>

                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 text-white space-y-3" data-id="element-18448">
                  <div className="flex justify-between items-start" data-id="element-18449">
                    <CreditCard className="w-8 h-8 text-slate-400" data-id="element-18450" />
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider" data-id="element-18451">
                      {finalAmount.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                  <div className="text-lg font-mono tracking-widest" data-id="element-18452">
                    {cardNumber || '•••• •••• •••• ••••'}
                  </div>
                  <div className="flex justify-between text-xs text-slate-400" data-id="element-18453">
                    <span data-id="element-18454">{cardName || 'CARDHOLDER NAME'}</span>
                    <span data-id="element-18455">{cardExpiry || 'MM/YY'}</span>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-18456">
                  <div data-id="element-18457">
                    <label className="text-[10px] text-slate-500 font-medium mb-1 block" data-id="element-18458">
                      {t('userdash.credits.cardNumber')}
                    </label>
                    <input type="text" value={cardNumber} placeholder="0000 0000 0000 0000" onChange={e => setCardNumber(formatCardNum(e.target.value))} className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 font-mono tracking-wider" maxLength={19} data-id="element-18459" />
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-18460">
                    <div data-id="element-18461">
                      <label className="text-[10px] text-slate-500 font-medium mb-1 block" data-id="element-18462">
                        {t('userdash.credits.cardExpiry')}
                      </label>
                      <input type="text" value={cardExpiry} placeholder="MM/YY" onChange={e => setCardExpiry(formatExp(e.target.value))} className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 font-mono" maxLength={5} data-id="element-18463" />
                    </div>
                    <div data-id="element-18464">
                      <label className="text-[10px] text-slate-500 font-medium mb-1 block" data-id="element-18465">
                        CVV
                      </label>
                      <input type="password" value={cardCvv} placeholder="•••" onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))} className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 font-mono" maxLength={4} data-id="element-18466" />
                    </div>
                  </div>
                  <div data-id="element-18467">
                    <label className="text-[10px] text-slate-500 font-medium mb-1 block" data-id="element-18468">
                      {t('userdash.credits.cardName')}
                    </label>
                    <input type="text" value={cardName} placeholder="IVAN IVANOV" onChange={e => setCardName(e.target.value.toUpperCase())} className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 uppercase" data-id="element-18469" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-18470">
                    <input type="checkbox" checked={saveCard} onChange={e => setSaveCard(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-18471" />
                    <span className="text-xs text-slate-600" data-id="element-18472">
                      {t('userdash.credits.saveCard')}
                    </span>
                  </label>
                </div>

                <div className="flex items-center gap-2 text-[10px] text-slate-400" data-id="element-18473">
                  <Shield className="w-3 h-3 shrink-0" data-id="element-18474" />{' '}
                  {t('userdash.credits.secureData')}
                </div>
                <Button size="sm" className="w-full text-xs" disabled={!isCardValid} onClick={() => setTopupStep(4)} data-id="element-18475">
                  {t('userdash.credits.payAmount', {
              amount: finalAmount.toLocaleString('ru-RU')
            })}
                </Button>
              </>}

            {/* Step 4: SMS */}
            {topupStep === 4 && <>
                <div className="flex items-center justify-between" data-id="element-18476">
                  <div className="flex items-center gap-2" data-id="element-18477">
                    <button onClick={() => setTopupStep(paymentMethod === 'card' ? 3 : 2)} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18478">
                      <ArrowLeft className="w-4 h-4" data-id="element-18479" />
                    </button>
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-18480">
                      {t('userdash.credits.smsConfirm')}
                    </h4>
                  </div>
                  <button onClick={resetTopup} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18481">
                    <X className="w-4 h-4" data-id="element-18482" />
                  </button>
                </div>

                <div className="text-center py-2" data-id="element-18483">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-3" data-id="element-18484">
                    <Smartphone className="w-7 h-7 text-indigo-600" data-id="element-18485" />
                  </div>
                  <p className="text-sm text-slate-700" data-id="element-18486">
                    {t('userdash.credits.enterSms')}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-18487">
                    {t('userdash.credits.sentTo')} +7 (•••) •••-••-42
                  </p>
                </div>

                <div className="flex justify-center gap-2" data-id="element-18488">
                  {[0, 1, 2, 3].map(i => <input key={i} type="text" maxLength={1} value={smsCode[i] || ''} onChange={e => {
              const digit = e.target.value.replace(/\D/g, '');
              const arr = smsCode.split('');
              arr[i] = digit;
              setSmsCode(arr.join(''));
              if (digit && i < 3) {
                const next = e.target.nextElementSibling as HTMLInputElement;
                if (next) next.focus();
              }
            }} onKeyDown={e => {
              if (e.key === 'Backspace' && !smsCode[i] && i > 0) {
                const prev = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                if (prev) prev.focus();
              }
            }} className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors" data-id="element-18489" />)}
                </div>

                <div className="text-center" data-id="element-18490">
                  {smsTimer > 0 ? <span className="text-[10px] text-slate-400" data-id="element-18491">
                      {t('userdash.credits.resendIn', {
                seconds: smsTimer.toString()
              })}
                    </span> : <button onClick={() => {
              setSmsTimer(60);
              showToast(t('userdash.credits.codeResent'));
            }} className="text-[10px] text-indigo-600 hover:underline" data-id="element-18492">
                      {t('userdash.credits.resendCode')}
                    </button>}
                </div>

                <Button size="sm" className="w-full text-xs" disabled={smsCode.replace(/\s/g, '').length < 4} onClick={() => setTopupStep(5)} data-id="element-18493">
                  {t('userdash.credits.confirm')}
                </Button>
              </>}

            {/* Step 5: Processing */}
            {topupStep === 5 && <div className="text-center py-8" data-id="element-18494">
                <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4" data-id="element-18495">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" data-id="element-18496" />
                </div>
                <h4 className="font-bold text-slate-900 text-sm" data-id="element-18497">
                  {t('userdash.credits.processing')}
                </h4>
                <p className="text-[10px] text-slate-400 mt-1" data-id="element-18498">
                  {t('userdash.credits.doNotClose')}
                </p>
                <div className="mt-4 flex justify-center gap-1" data-id="element-18499">
                  {[0, 1, 2].map(i => <div key={i} className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" style={{
              animationDelay: `${i * 0.3}s`
            }} data-id="element-18500" />)}
                </div>
              </div>}

            {/* Step 6: Success */}
            {topupStep === 6 && <>
                <div className="text-center py-4" data-id="element-18501">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3" data-id="element-18502">
                    <Check className="w-8 h-8 text-emerald-600" data-id="element-18503" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-lg" data-id="element-18504">
                    {t('userdash.credits.paymentSuccess')}
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-18505">
                    {t('userdash.credits.balanceToppedUp', {
                amount: finalAmount.toLocaleString('ru-RU')
              })}
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 space-y-2 border border-slate-100" data-id="element-18506">
                  <div className="flex items-center gap-2 mb-2" data-id="element-18507">
                    <FileText className="w-4 h-4 text-slate-400" data-id="element-18508" />
                    <span className="text-xs font-bold text-slate-700" data-id="element-18509">
                      {t('userdash.credits.receipt')}
                    </span>
                  </div>
                  <div className="space-y-1.5" data-id="element-18510">
                    <div className="flex justify-between text-[11px]" data-id="element-18511">
                      <span className="text-slate-500" data-id="element-18512">
                        {t('userdash.credits.operation')}
                      </span>
                      <span className="text-slate-900" data-id="element-18513">
                        {t('userdash.credits.topupBalance')}
                      </span>
                    </div>
                    <div className="flex justify-between text-[11px]" data-id="element-18514">
                      <span className="text-slate-500" data-id="element-18515">
                        {t('userdash.credits.amount')}
                      </span>
                      <span className="text-slate-900 font-bold" data-id="element-18516">
                        {finalAmount.toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                    <div className="flex justify-between text-[11px]" data-id="element-18517">
                      <span className="text-slate-500" data-id="element-18518">
                        {t('userdash.credits.method')}
                      </span>
                      <span className="text-slate-900" data-id="element-18519">
                        {PAYMENT_METHODS.find(m => m.id === paymentMethod)?.label || ''}
                        {paymentMethod === 'card' && cardNumber ? ` •••• ${cardNumber.slice(-4)}` : ''}
                      </span>
                    </div>
                    <div className="flex justify-between text-[11px]" data-id="element-18520">
                      <span className="text-slate-500" data-id="element-18521">
                        {t('userdash.credits.date')}
                      </span>
                      <span className="text-slate-900" data-id="element-18522">
                        {new Date().toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </span>
                    </div>
                    <div className="flex justify-between text-[11px]" data-id="element-18523">
                      <span className="text-slate-500" data-id="element-18524">
                        {t('userdash.credits.transaction')}
                      </span>
                      <span className="text-slate-900 font-mono text-[10px]" data-id="element-18525">
                        TX-{String(Date.now()).slice(-4)}
                      </span>
                    </div>
                    <div className="flex justify-between text-[11px]" data-id="element-18526">
                      <span className="text-slate-500" data-id="element-18527">
                        {t('userdash.credits.status')}
                      </span>
                      <Badge variant="success" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18528">
                        <CheckCircle2 className="w-3 h-3 mr-0.5" data-id="element-18529" />{' '}
                        {t('userdash.credits.success')}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2" data-id="element-18530">
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => showToast(t('userdash.credits.receiptSent'))} data-id="element-18531">
                    <Receipt className="w-3.5 h-3.5 mr-1" data-id="element-18532" />{' '}
                    {t('userdash.credits.receiptToEmail')}
                  </Button>
                  <Button size="sm" className="flex-1 text-xs" onClick={resetTopup} data-id="element-18533">
                    {t('userdash.credits.done')}
                  </Button>
                </div>
              </>}
          </div>
        </div>}

      {/* Filters + Sort */}
      <div className="flex items-center justify-between gap-2" data-id="element-18534">
        <div className="flex items-center gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden pb-0.5" data-id="element-18535">
          {FILTER_TABS.map(f => {
          const count = f.key === 'all' ? transactions.length : transactions.filter(t => t.type === f.key).length;
          return <button key={f.key} onClick={() => setFilter(f.key)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors shrink-0 ${filter === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-18536">
                {f.label} ({count})
              </button>;
        })}
        </div>
        <button onClick={() => setSortOrder(sortOrder === 'new' ? 'old' : 'new')} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors shrink-0" data-id="element-18537">
          <ArrowUpDown className="w-3 h-3" data-id="element-18538" />{' '}
          {sortOrder === 'new' ? t('userdash.credits.sortNew') : t('userdash.credits.sortOld')}
        </button>
      </div>

      {/* Transactions */}
      {filtered.length > 0 ? <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18539">
          {filtered.map(tx => {
        const isPositive = tx.amount >= 0;
        const sc = STATUS_CONFIG[tx.status];
        const isExpanded = expandedTx === tx.id;
        return <div key={tx.id} data-id="element-18540">
                <button onClick={() => setExpandedTx(isExpanded ? null : tx.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/50 transition-colors" data-id="element-18541">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isPositive ? 'bg-emerald-50' : 'bg-slate-100'}`} data-id="element-18542">
                    {isPositive ? <ArrowDownLeft className="w-4 h-4 text-emerald-600" data-id="element-18543" /> : <ArrowUpRight className="w-4 h-4 text-slate-500" data-id="element-18544" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-18545">
                    <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-18546">
                      {tx.description}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5" data-id="element-18547">
                      {tx.date.split(',')[0]} · {tx.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0" data-id="element-18548">
                    <span className={`font-bold text-sm ${isPositive ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-18549">
                      {formatAmount(tx.amount)}
                    </span>
                    <Badge variant={sc.variant} size="sm" className="text-[9px] py-0 h-5 px-1.5 hidden sm:flex items-center gap-0.5" data-id="element-18550">
                      {sc.icon} {sc.label}
                    </Badge>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-18551" />
                  </div>
                </button>
                {isExpanded && <div className="px-4 pb-3 -mt-1" data-id="element-18552">
                    <div className="bg-slate-50 rounded-lg p-3 space-y-1.5" data-id="element-18553">
                      <div className="flex justify-between text-xs" data-id="element-18554">
                        <span className="text-slate-500" data-id="element-18555">
                          {t('userdash.credits.dateTime')}
                        </span>
                        <span className="text-slate-700" data-id="element-18556">{tx.date}</span>
                      </div>
                      <div className="flex justify-between text-xs" data-id="element-18557">
                        <span className="text-slate-500" data-id="element-18558">
                          {t('userdash.credits.transaction')}
                        </span>
                        <span className="text-slate-700 font-mono" data-id="element-18559">
                          {tx.id}
                        </span>
                      </div>
                      {tx.method && <div className="flex justify-between text-xs" data-id="element-18560">
                          <span className="text-slate-500" data-id="element-18561">
                            {t('userdash.credits.method')}
                          </span>
                          <span className="text-slate-700" data-id="element-18562">{tx.method}</span>
                        </div>}
                      <div className="flex justify-between text-xs" data-id="element-18563">
                        <span className="text-slate-500" data-id="element-18564">
                          {t('userdash.credits.status')}
                        </span>
                        <Badge variant={sc.variant} size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18565">
                          {sc.icon} {sc.label}
                        </Badge>
                      </div>
                      {tx.orderId && <div className="flex justify-between text-xs" data-id="element-18566">
                          <span className="text-slate-500" data-id="element-18567">
                            {t('userdash.credits.order')}
                          </span>
                          <button onClick={() => onNavigate('orders')} className="text-indigo-600 hover:underline flex items-center gap-0.5" data-id="element-18568">
                            {tx.orderId}{' '}
                            <ExternalLink className="w-2.5 h-2.5" data-id="element-18569" />
                          </button>
                        </div>}
                      <div className="pt-1.5 border-t border-slate-200" data-id="element-18570">
                        <button onClick={() => showToast(t('userdash.credits.receiptSentId', {
                  id: tx.id
                }))} className="text-[10px] text-indigo-600 hover:underline flex items-center gap-1" data-id="element-18571">
                          <Receipt className="w-3 h-3" data-id="element-18572" />{' '}
                          {t('userdash.credits.sendReceipt')}
                        </button>
                      </div>
                    </div>
                  </div>}
              </div>;
      })}
        </div> : <div className="text-center py-12" data-id="element-18573">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3" data-id="element-18574">
            <Wallet className="w-7 h-7 text-slate-300" data-id="element-18575" />
          </div>
          <h3 className="font-bold text-slate-900 mb-1 text-sm" data-id="element-18576">
            {t('userdash.credits.noOperations')}
          </h3>
          <p className="text-xs text-slate-500" data-id="element-18577">
            {t('userdash.credits.noOperationsDesc')}
          </p>
        </div>}

      {/* Stats */}
      <div className="flex gap-3" data-id="element-18578">
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-18579">
          <div className="text-lg font-bold text-slate-900" data-id="element-18580">
            {totalTopup.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-18581">
            {t('userdash.credits.toppedUp')}
          </div>
        </div>
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-18582">
          <div className="text-lg font-bold text-slate-900" data-id="element-18583">
            {totalSpent.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-18584">
            {t('userdash.credits.spent')}
          </div>
        </div>
        <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-center" data-id="element-18585">
          <div className="text-lg font-bold text-emerald-600" data-id="element-18586">
            {totalRefund.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-18587">
            {t('userdash.credits.refunds')}
          </div>
        </div>
      </div>
    </div>;
}